import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminUser } from '../types'

interface AdminUserRowProps {
  user: AdminUser
  onSuspend: (id: string) => void
  onReactivate: (id: string) => void
  onDelete: (id: string) => void
  onForceReset: (id: string) => void
  onExport: (id: string) => void
  isLoading?: boolean
}

export function AdminUserRow({
  user,
  onSuspend,
  onReactivate,
  onDelete,
  onForceReset,
  onExport,
  isLoading,
}: AdminUserRowProps) {
  const { t } = useTranslation()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const statusLabel = user.deleted_at
    ? t('admin.status.deleted')
    : user.is_suspended
    ? t('admin.status.suspended')
    : t('admin.status.active')

  const statusClass = user.deleted_at
    ? 'bg-surface-100 text-neutral-600'
    : user.is_suspended
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-mindeia-success-light text-mindeia-success'

  return (
    <tr className="border-b border-surface-200 last:border-0">
      <td className="py-3 px-4">
        <div className="font-medium text-neutral-900 text-sm">{user.name}</div>
      </td>
      <td className="py-3 px-4 text-sm text-neutral-600">{user.email}</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
          {statusLabel}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-neutral-600">
        {new Date(user.created_at).toLocaleDateString('fr-FR')}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 flex-wrap">
          {!user.deleted_at && (
            <>
              {user.is_suspended ? (
                <button
                  onClick={() => onReactivate(user.id)}
                  disabled={isLoading}
                  aria-label={`${t('admin.reactivate')} — ${user.email}`}
                  className="min-h-[44px] px-3 py-1 text-xs rounded border border-mindeia-success text-mindeia-success hover:bg-mindeia-success-light disabled:opacity-50 transition-colors"
                >
                  {t('admin.reactivate')}
                </button>
              ) : (
                <button
                  onClick={() => onSuspend(user.id)}
                  disabled={isLoading}
                  aria-label={`${t('admin.suspend')} — ${user.email}`}
                  className="min-h-[44px] px-3 py-1 text-xs rounded border border-yellow-500 text-yellow-700 hover:bg-yellow-50 disabled:opacity-50 transition-colors"
                >
                  {t('admin.suspend')}
                </button>
              )}
              <button
                onClick={() => onForceReset(user.id)}
                disabled={isLoading}
                aria-label={`${t('admin.force_reset')} — ${user.email}`}
                className="min-h-[44px] px-3 py-1 text-xs rounded border border-surface-200 text-neutral-700 hover:bg-surface-50 disabled:opacity-50 transition-colors"
              >
                {t('admin.force_reset')}
              </button>
              <button
                onClick={() => onExport(user.id)}
                disabled={isLoading}
                aria-label={`${t('admin.export')} — ${user.email}`}
                className="min-h-[44px] px-3 py-1 text-xs rounded border border-surface-200 text-neutral-700 hover:bg-surface-50 disabled:opacity-50 transition-colors"
              >
                {t('admin.export')}
              </button>
              {confirmDelete ? (
                <span className="inline-flex items-center gap-1">
                  <button
                    onClick={() => { onDelete(user.id); setConfirmDelete(false) }}
                    disabled={isLoading}
                    aria-label={`${t('common.confirm')} suppression — ${user.email}`}
                    className="min-h-[44px] px-3 py-1 text-xs rounded bg-mindeia-error text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {t('common.confirm')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    aria-label={t('common.cancel')}
                    className="min-h-[44px] px-3 py-1 text-xs rounded border border-surface-200 text-neutral-700 hover:bg-surface-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={isLoading}
                  aria-label={`${t('admin.delete')} — ${user.email}`}
                  className="min-h-[44px] px-3 py-1 text-xs rounded border border-mindeia-error text-mindeia-error hover:bg-mindeia-error-light disabled:opacity-50 transition-colors"
                >
                  {t('admin.delete')}
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  )
}
